import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Monitor,
  Loader2,
  ArrowRight,
  RefreshCw,
  CheckCircle2,
  XCircle,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useKioskDevice, type KioskDeviceInfo } from '@/hooks/useKiosk';
import { toast } from 'sonner';

export default function KioskSetup() {
  const navigate = useNavigate();
  const { registerDevice, validateToken, clearDevice, isRegistered } = useKioskDevice();
  
  const [deviceCode, setDeviceCode] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isRegistering, setIsRegistering] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [deviceInfo, setDeviceInfo] = useState<KioskDeviceInfo | null>(null);

  // Check for existing registration on mount
  useEffect(() => {
    const checkRegistration = async () => {
      if (isRegistered()) {
        const info = await validateToken();
        if (info) {
          setDeviceInfo(info);
        }
      }
      setIsLoading(false);
    };

    checkRegistration();
  }, []);

  // Handle device code input - auto uppercase and limit to 6 chars
  const handleCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 6);
    setDeviceCode(value);
    setError(null);
  };

  // Register device
  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (deviceCode.length !== 6) {
      setError('Please enter a 6-character device code');
      return;
    }

    setIsRegistering(true);
    setError(null);

    try {
      const info = await registerDevice(deviceCode);
      setDeviceInfo(info);
      toast.success('Device registered!', {
        description: `Connected as ${info.device_name} at ${info.location_name}`,
      });
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to register device';
      setError(message);
    } finally {
      setIsRegistering(false);
    }
  };

  // Disconnect device
  const handleDisconnect = () => {
    clearDevice();
    setDeviceInfo(null);
    setDeviceCode('');
    toast.success('Device disconnected', {
        description: 'This kiosk is no longer registered',
      });
  };

  // Start kiosk mode
  const handleStartKiosk = () => {
    navigate('/kiosk');
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="h-12 w-12 text-blaze-500 animate-spin" />
          <p className="text-white/60">Checking device registration...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-6">
      <div className="w-full max-w-md">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="w-20 h-20 mx-auto bg-blaze-500/20 rounded-2xl flex items-center justify-center mb-6">
            <Monitor className="h-10 w-10 text-blaze-500" />
          </div>
          <h1 className="text-3xl font-bold text-white mb-2">Kiosk Setup</h1>
          <p className="text-white/60">
            {deviceInfo 
              ? 'This device is registered as a kiosk'
              : 'Enter your 6-character device code to register this kiosk'
            }
          </p>
        </div>

        {deviceInfo ? (
          /* Registered State */
          <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-8 border border-white/10">
            <div className="flex items-center gap-3 mb-6">
              <CheckCircle2 className="h-6 w-6 text-green-500" />
              <span className="text-green-400 font-medium">Device Registered</span>
            </div>

            <div className="space-y-4 mb-8">
              <div>
                <label className="text-white/40 text-sm">Device Name</label>
                <p className="text-white text-lg font-medium">{deviceInfo.device_name}</p>
              </div>
              <div>
                <label className="text-white/40 text-sm">Location</label>
                <p className="text-white text-lg font-medium">{deviceInfo.location_name}</p>
              </div>
              <div>
                <label className="text-white/40 text-sm">Settings</label>
                <div className="flex flex-wrap gap-2 mt-1">
                  {deviceInfo.settings.allow_dine_in && (
                    <span className="px-2 py-1 bg-white/10 rounded text-sm text-white/80">Dine-in</span>
                  )}
                  {deviceInfo.settings.allow_takeout && (
                    <span className="px-2 py-1 bg-white/10 rounded text-sm text-white/80">Takeout</span>
                  )}
                  {deviceInfo.settings.show_prices && (
                    <span className="px-2 py-1 bg-white/10 rounded text-sm text-white/80">Prices visible</span>
                  )}
                </div>
              </div>
            </div>

            <div className="space-y-3">
              <Button
                size="lg"
                className="w-full h-14 text-lg bg-blaze-500 hover:bg-blaze-600"
                onClick={handleStartKiosk}
              >
                Start Kiosk Mode
                <ArrowRight className="h-5 w-5 ml-2" />
              </Button>
              <Button
                variant="ghost"
                className="w-full text-white/60 hover:text-white hover:bg-white/10"
                onClick={handleDisconnect}
              >
                <XCircle className="h-4 w-4 mr-2" />
                Disconnect Device
              </Button>
            </div>
          </div>
        ) : (
          /* Registration Form */
          <form onSubmit={handleRegister} className="bg-white/5 backdrop-blur-sm rounded-2xl p-8 border border-white/10">
            {error && (
              <div className="mb-6 p-4 bg-red-500/10 border border-red-500/20 rounded-xl text-red-400 text-sm">
                {error}
              </div>
            )}

            <div className="mb-6">
              <label className="block text-white/80 text-sm font-medium mb-2">
                Device Code
              </label>
              <Input
                type="text"
                value={deviceCode}
                onChange={handleCodeChange}
                placeholder="XXXXXX"
                className="h-16 text-center text-3xl font-mono tracking-[0.5em] bg-white/10 border-white/20 text-white placeholder:text-white/30 uppercase"
                autoFocus
                autoComplete="off"
              />
              <p className="text-white/40 text-sm mt-2 text-center">
                Get this code from your admin dashboard
              </p>
            </div>

            <Button
              type="submit"
              size="lg"
              className="w-full h-14 text-lg bg-blaze-500 hover:bg-blaze-600"
              disabled={deviceCode.length !== 6 || isRegistering}
            >
              {isRegistering ? (
                <>
                  <Loader2 className="h-5 w-5 animate-spin mr-2" />
                  Registering...
                </>
              ) : (
                <>
                  Register Device
                  <ArrowRight className="h-5 w-5 ml-2" />
                </>
              )}
            </Button>

            <div className="mt-6 pt-6 border-t border-white/10 text-center">
              <p className="text-white/40 text-sm">
                Need help? Ask your manager for the device code or visit the Team → Kiosks section in the admin dashboard.
              </p>
            </div>
          </form>
        )}

        {/* Refresh option */}
        {deviceInfo && (
          <div className="mt-6 text-center">
            <button
              onClick={async () => {
                setIsLoading(true);
                const info = await validateToken();
                if (info) {
                  setDeviceInfo(info);
                  toast.success('Settings refreshed');
                }
                setIsLoading(false);
              }}
              className="text-white/40 hover:text-white/60 text-sm inline-flex items-center gap-1"
            >
              <RefreshCw className="h-3 w-3" />
              Refresh settings
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

